import React from 'react';

const toRupees = (paise) => {
  if (!paise && paise !== 0) return '₹0.00';
  return `₹${(paise / 100).toFixed(2)}`;
};

// Icon component for displaying SVG icons
const Icon = ({ type, size = '16' }) => {
  const icons = {
    orders: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 11l3 3L22 4"></path>
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
    ),
    products: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="9" cy="21" r="1"></circle>
        <circle cx="20" cy="21" r="1"></circle>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
      </svg>
    ),
    bill: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20m10-10H2"></path>
        <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path>
      </svg>
    ),
    average: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="2" x2="12" y2="22"></line>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
      </svg>
    ),
    discount: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"></path>
      </svg>
    ),
    calendar: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
    ),
    consume: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 1v22"></path>
        <path d="M17 5H9.5a4.5 4.5 0 0 0 0 9h5a4.5 4.5 0 0 1 0 9H4"></path>
      </svg>
    ),
    wallet: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <path d="M1 10h22"></path>
        <circle cx="17" cy="15" r="1.5"></circle>
      </svg>
    ),
  };
  return icons[type] || null;
};

const CustomerInfoCompact = ({ customerData, ledgerData = [] }) => {
  if (!customerData) return null;

  // Calculate wallet stats from ledger data
  const calculateWalletStats = () => {
    let totalPurchaseCount = 0;
    let totalPurchaseAmount = 0;
    let totalConsumeCount = 0;
    let totalConsumeAmount = 0;
    let lastPurchaseDate = null;
    let lastConsumeDate = null;
    let currentBalance = 0;

    if (Array.isArray(ledgerData) && ledgerData.length > 0) {
      // Sort by TransTime to find latest transactions
      const sortedData = [...ledgerData].sort((a, b) =>
        new Date(b.TransTime) - new Date(a.TransTime)
      );

      sortedData.forEach((transaction) => {
        if (transaction.AcctTransType === 'CREDIT') {
          // Credit = Wallet Purchase (Money added to wallet)
          totalPurchaseCount += 1;
          totalPurchaseAmount += transaction.Amount || 0;
          if (!lastPurchaseDate) {
            lastPurchaseDate = transaction.TransTime;
          }
        } else if (transaction.AcctTransType === 'DEBIT') {
          // Debit = Consume (Customer spent money)
          totalConsumeCount += 1;
          totalConsumeAmount += transaction.Amount || 0;
          if (!lastConsumeDate) {
            lastConsumeDate = transaction.TransTime;
          }
        }
      });

      // Get the last transaction's closing balance
      currentBalance = sortedData[0]?.Closing || 0;
    }

    return {
      totalPurchaseCount,
      totalPurchaseAmount,
      totalConsumeCount,
      totalConsumeAmount,
      lastPurchaseDate,
      lastConsumeDate,
      currentBalance,
    };
  };

  const walletStats = calculateWalletStats();

  const {
    name,
    phone_number,
    addresses = [],
  } = customerData;

  const fullAddress = addresses[0]?.full_address || 'N/A';

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const containerStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    padding: '8px',
  };

  const headerBorderStyle = {
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e5e7eb',
  };

  const nameStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: 0,
  };

  const mobileStyle = {
    fontSize: '12px',
    color: '#4b5563',
    margin: '4px 0 0 0',
  };

  const addressBorderStyle = {
    marginBottom: '8px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e5e7eb',
  };

  const addressStyle = {
    fontSize: '12px',
    color: '#374151',
    lineHeight: '1.4',
    margin: 0,
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '4px',
    fontSize: '12px',
  };

  const cardStyle = {
    backgroundColor: '#f9fafb',
    padding: '8px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  };

  const cardLabelStyle = {
    color: '#6b7280',
    fontWeight: '500',
    fontSize: '11px',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };

  const cardValueStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: 0,
  };

  return (
    <div style={containerStyle}>
      {/* Header Row - Name and Mobile */}
      <div style={headerBorderStyle}>
        <h3 style={nameStyle}>{name}</h3>
        <p style={mobileStyle}>Mobile: {phone_number}</p>
      </div>

      {/* Address */}
      <div style={addressBorderStyle}>
        <p style={addressStyle}>
          <strong>Address:</strong> {fullAddress}
        </p>
      </div>

      {/* Statistics Grid - Wallet Cards */}
      <div style={gridStyle}>
        {/* Total Purchase Card */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#059669', width: '18px', height: '18px' }}>
              <Icon type="wallet" size="18" />
            </span>
            <p style={cardLabelStyle}>Total Purchase</p>
          </div>
          <p style={{ ...cardValueStyle, color: '#059669' }}>
            {toRupees(walletStats.totalPurchaseAmount)}
          </p>
          <p style={{ ...cardValueStyle, fontSize: '11px', color: '#6b7280' }}>
            ({walletStats.totalPurchaseCount} {walletStats.totalPurchaseCount === 1 ? 'txn' : 'txns'})
          </p>
        </div>

        {/* Total Consume Card */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#dc2626', width: '18px', height: '18px' }}>
<Icon type="wallet" size="18" />           
            </span>
            <p style={cardLabelStyle}>Total Consume</p>
          </div>
          <p style={{ ...cardValueStyle, color: '#dc2626' }}>
            {toRupees(walletStats.totalConsumeAmount)}
          </p>
          <p style={{ ...cardValueStyle, fontSize: '11px', color: '#6b7280' }}>
            ({walletStats.totalConsumeCount} {walletStats.totalConsumeCount === 1 ? 'txn' : 'txns'})
          </p>
        </div>

        {/* Last Purchase Date Card */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#3b82f6', width: '18px', height: '18px' }}>
              <Icon type="calendar" size="18" />
            </span>
            <p style={cardLabelStyle}>Last Purchase</p>
          </div>
          <p style={{ ...cardValueStyle, fontSize: '12px' }}>
            {formatDate(walletStats.lastPurchaseDate)}
          </p>
        </div>

        {/* Last Consume Date Card */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#f59e0b', width: '18px', height: '18px' }}>
              <Icon type="calendar" size="18" />
            </span>
            <p style={cardLabelStyle}>Last Consumed</p>
          </div>
          <p style={{ ...cardValueStyle, fontSize: '12px' }}>
            {formatDate(walletStats.lastConsumeDate)}
          </p>
        </div>

        {/* Wallet Balance Card */}
        <div style={{
          ...cardStyle,
          backgroundColor: '#f0fdf4',
          border: '1.5px solid #86efac',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#16a34a', width: '18px', height: '18px' }}>
              <Icon type="wallet" size="18" />
            </span>
            <p style={{ ...cardLabelStyle, color: '#16a34a' }}>Current Wallet Balance</p>
          </div>
          <p style={{ ...cardValueStyle, color: '#16a34a', fontSize: '18px' }}>
            {toRupees(walletStats.currentBalance)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoCompact;
